
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import { ReportTemplate } from '../components/ReportTemplateSelector';
import { ReportCustomizations } from '../components/ReportCustomizer';

export interface ProfessionalExportOptions {
  template: ReportTemplate;
  customizations: ReportCustomizations;
  title: string;
  content: string;
  organizationName?: string;
}

// Generate professionally formatted PDF
export async function generateProfessionalPDF(
  options: ProfessionalExportOptions,
  fileName: string = 'report.pdf'
): Promise<string> {
  const { customizations, title, content, organizationName } = options;
  
  // Create a temporary container with professional styling
  const container = document.createElement('div');
  container.style.cssText = `
    font-family: ${customizations.fontFamily};
    background: white;
    padding: 60px;
    max-width: 210mm;
    margin: 0 auto;
    color: #1a1a1a;
    line-height: 1.6;
  `;

  // Header with branding
  const header = document.createElement('div');
  header.style.cssText = `
    border-bottom: 4px solid ${customizations.primaryColor};
    padding-bottom: 20px;
    margin-bottom: 40px;
  `;
  
  if (organizationName || customizations.organizationName) {
    const orgElement = document.createElement('div');
    orgElement.style.cssText = `
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
    `;
    orgElement.textContent = organizationName || customizations.organizationName || '';
    header.appendChild(orgElement);
  }

  const titleElement = document.createElement('h1');
  titleElement.style.cssText = `
    font-size: 32px;
    font-weight: bold;
    color: ${customizations.primaryColor};
    margin: 0;
    line-height: 1.2;
  `;
  titleElement.textContent = title;
  header.appendChild(titleElement);

  const dateElement = document.createElement('div');
  dateElement.style.cssText = `
    font-size: 12px;
    color: #888;
    margin-top: 12px;
  `;
  dateElement.textContent = `Generated on ${new Date().toLocaleDateString()}`;
  header.appendChild(dateElement);

  container.appendChild(header);

  // Table of Contents (if enabled)
  if (customizations.includeTableOfContents) {
    const tocSection = document.createElement('div');
    tocSection.style.cssText = `
      margin-bottom: 40px;
      padding: 20px;
      background: #f8f9fa;
      border-radius: 8px;
    `;
    
    const tocTitle = document.createElement('h2');
    tocTitle.style.cssText = `
      font-size: 20px;
      color: ${customizations.accentColor};
      margin: 0 0 16px 0;
    `;
    tocTitle.textContent = 'Table of Contents';
    tocSection.appendChild(tocTitle);

    // Extract headers for TOC
    const headers = content.split('\n').filter(line => line.startsWith('#'));
    headers.slice(0, 8).forEach((header) => {
      const tocItem = document.createElement('div');
      tocItem.style.cssText = `
        padding: 4px 0;
        color: #555;
        font-size: 14px;
      `;
      const level = header.match(/^#+/)?.[0].length || 1;
      const indent = (level - 1) * 20;
      tocItem.style.paddingLeft = `${indent}px`;
      tocItem.textContent = `${header.replace(/^#+\s/, '')}`;
      tocSection.appendChild(tocItem);
    });

    container.appendChild(tocSection);
  }

  // Executive Summary (if enabled)
  if (customizations.includeExecutiveSummary) {
    const execSection = document.createElement('div');
    execSection.style.cssText = `
      margin-bottom: 40px;
      padding: 24px;
      border: 1px solid #e0e0e0;
      border-left: 4px solid ${customizations.accentColor};
      background: #fafafa;
    `;
    
    const execTitle = document.createElement('h2');
    execTitle.style.cssText = `
      font-size: 20px;
      color: ${customizations.accentColor};
      margin: 0 0 16px 0;
    `;
    execTitle.textContent = 'Executive Summary';
    execSection.appendChild(execTitle);

    const execContent = document.createElement('div');
    execContent.style.cssText = `
      font-size: 14px;
      color: #555;
      line-height: 1.7;
    `;
    execContent.innerHTML = `
      <p>This comprehensive political campaign analysis provides strategic insights and actionable recommendations 
      based on the Playing to Win framework. The analysis covers key areas including stakeholder mapping, 
      strategic positioning, and implementation roadmaps designed to maximize campaign effectiveness.</p>
    `;
    execSection.appendChild(execContent);

    container.appendChild(execSection);
  }

  // Main content with professional formatting
  const mainContent = document.createElement('div');
  const lines = content.split('\n');
  
  lines.forEach((line) => {
    if (line.trim() === '') {
      const spacer = document.createElement('div');
      spacer.style.height = '12px';
      mainContent.appendChild(spacer);
      return;
    }

    if (line.startsWith('# ')) {
      const h1 = document.createElement('h1');
      h1.style.cssText = `
        font-size: 24px;
        font-weight: bold;
        color: ${customizations.primaryColor};
        margin: 32px 0 16px 0;
        padding-bottom: 8px;
        border-bottom: 2px solid ${customizations.primaryColor};
      `;
      h1.textContent = line.substring(2);
      mainContent.appendChild(h1);
    } else if (line.startsWith('## ')) {
      const h2 = document.createElement('h2');
      h2.style.cssText = `
        font-size: 20px;
        font-weight: bold;
        color: ${customizations.accentColor};
        margin: 24px 0 12px 0;
      `;
      h2.textContent = line.substring(3);
      mainContent.appendChild(h2);
    } else if (line.startsWith('### ')) {
      const h3 = document.createElement('h3');
      h3.style.cssText = `
        font-size: 16px;
        font-weight: bold;
        color: #333;
        margin: 20px 0 8px 0;
      `;
      h3.textContent = line.substring(4);
      mainContent.appendChild(h3);
    } else if (line.startsWith('- ')) {
      const li = document.createElement('div');
      li.style.cssText = `
        margin: 6px 0 6px 24px;
        position: relative;
        font-size: 14px;
        line-height: 1.6;
      `;
      li.innerHTML = `<span style="position: absolute; left: -16px; color: ${customizations.accentColor};">•</span>${line.substring(2)}`;
      mainContent.appendChild(li);
    } else if (line.trim()) {
      const p = document.createElement('p');
      p.style.cssText = `
        margin: 12px 0;
        font-size: 14px;
        line-height: 1.7;
        color: #333;
      `;
      p.textContent = line;
      mainContent.appendChild(p);
    }
  });

  container.appendChild(mainContent);

  // Add to document temporarily for rendering
  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: container.scrollWidth,
      height: container.scrollHeight
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Add page numbers if enabled
    if (customizations.pageNumbers) {
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(customizations.primaryColor);
        pdf.text(`Page ${i} of ${pageCount}`, 105, 287, { align: 'center' });
      }
    }

    pdf.save(fileName);
    return fileName;
  } finally {
    document.body.removeChild(container);
  }
}

// Generate professionally formatted DOCX
export async function generateProfessionalDOCX(
  options: ProfessionalExportOptions,
  fileName: string = 'report.docx'
): Promise<string> {
  const { customizations, title, content, organizationName } = options;
  
  const children: Paragraph[] = [];

  // Header section
  if (organizationName || customizations.organizationName) {
    children.push(new Paragraph({
      children: [new TextRun({
        text: organizationName || customizations.organizationName || '',
        size: 20,
        color: '666666'
      })],
      spacing: { after: 200 }
    }));
  }

  children.push(new Paragraph({
    children: [new TextRun({
      text: title,
      size: 48,
      bold: true,
      color: customizations.primaryColor.replace('#', '')
    })],
    heading: HeadingLevel.TITLE,
    spacing: { after: 400 }
  }));

  children.push(new Paragraph({
    children: [new TextRun({
      text: `Generated on ${new Date().toLocaleDateString()}`,
      size: 18,
      color: '888888'
    })],
    spacing: { after: 600 }
  }));

  // Table of Contents placeholder
  if (customizations.includeTableOfContents) {
    children.push(new Paragraph({
      children: [new TextRun({
        text: 'Table of Contents',
        size: 32,
        bold: true,
        color: customizations.accentColor.replace('#', '')
      })],
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 }
    }));

    children.push(new Paragraph({
      children: [new TextRun({
        text: '[Table of contents will be generated when opened in Microsoft Word]',
        size: 20,
        italics: true,
        color: '666666'
      })],
      spacing: { after: 600 }
    }));
  }

  // Executive Summary
  if (customizations.includeExecutiveSummary) {
    children.push(new Paragraph({
      children: [new TextRun({
        text: 'Executive Summary',
        size: 32,
        bold: true,
        color: customizations.accentColor.replace('#', '')
      })],
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 }
    }));

    children.push(new Paragraph({
      children: [new TextRun({
        text: 'This comprehensive political campaign analysis provides strategic insights and actionable recommendations based on the Playing to Win framework. The analysis covers key areas including stakeholder mapping, strategic positioning, and implementation roadmaps designed to maximize campaign effectiveness.',
        size: 22
      })],
      spacing: { after: 400 }
    }));
  }

  // Process main content
  const lines = content.split('\n');
  
  lines.forEach(line => {
    if (line.trim() === '') {
      children.push(new Paragraph({ spacing: { after: 200 } }));
      return;
    }

    if (line.startsWith('# ')) {
      children.push(new Paragraph({
        children: [new TextRun({
          text: line.substring(2),
          size: 32,
          bold: true,
          color: customizations.primaryColor.replace('#', '')
        })],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 }
      }));
    } else if (line.startsWith('## ')) {
      children.push(new Paragraph({
        children: [new TextRun({
          text: line.substring(3),
          size: 28,
          bold: true,
          color: customizations.accentColor.replace('#', '')
        })],
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 300, after: 200 }
      }));
    } else if (line.startsWith('### ')) {
      children.push(new Paragraph({
        children: [new TextRun({
          text: line.substring(4),
          size: 24,
          bold: true
        })],
        heading: HeadingLevel.HEADING_3,
        spacing: { before: 200, after: 200 }
      }));
    } else if (line.startsWith('- ')) {
      children.push(new Paragraph({
        children: [
          new TextRun('• '),
          new TextRun(line.substring(2))
        ],
        spacing: { before: 100, after: 100 },
        indent: { left: 720 }
      }));
    } else if (line.trim()) {
      children.push(new Paragraph({
        children: [new TextRun({
          text: line,
          size: 22
        })],
        spacing: { before: 100, after: 100 }
      }));
    }
  });

  const doc = new Document({
    sections: [{
      properties: {},
      children
    }]
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, fileName);
  return fileName;
}

// Generate PowerPoint slides (basic implementation)
export async function generateProfessionalPPTX(
  options: ProfessionalExportOptions,
  fileName: string = 'report.pptx'
): Promise<string> {
  const { title, content } = options;
  
  // For now, create a simplified version as HTML that can be copy-pasted into PowerPoint
  const slides = extractSlidesFromContent(content);
  const htmlSlides = generateSlidesHTML(title, slides, options);
  
  // Create a blob with HTML content that can be opened and copy-pasted
  const blob = new Blob([htmlSlides], { type: 'text/html' });
  saveAs(blob, fileName.replace('.pptx', '_slides.html'));
  
  return fileName.replace('.pptx', '_slides.html');
}

function extractSlidesFromContent(content: string): Array<{title: string, points: string[]}> {
  const lines = content.split('\n');
  const slides: Array<{title: string, points: string[]}> = [];
  let currentSlide: {title: string, points: string[]} | null = null;

  lines.forEach(line => {
    if (line.startsWith('# ') || line.startsWith('## ')) {
      if (currentSlide) {
        slides.push(currentSlide);
      }
      currentSlide = {
        title: line.replace(/^#+\s/, ''),
        points: []
      };
    } else if (line.startsWith('- ') && currentSlide) {
      currentSlide.points.push(line.substring(2));
    } else if (line.trim() && currentSlide && !line.startsWith('#')) {
      currentSlide.points.push(line);
    }
  });

  if (currentSlide) {
    slides.push(currentSlide);
  }

  return slides.slice(0, 10); // Limit to 10 slides for demo
}

function generateSlidesHTML(title: string, slides: Array<{title: string, points: string[]}>, options: ProfessionalExportOptions): string {
  const { customizations } = options;
  
  return `
<!DOCTYPE html>
<html>
<head>
    <title>${title} - Presentation Slides</title>
    <style>
        body { font-family: ${customizations.fontFamily}; margin: 0; padding: 20px; background: #f5f5f5; }
        .slide { 
            background: white; 
            width: 900px; 
            height: 600px; 
            margin: 20px auto; 
            padding: 60px; 
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            page-break-after: always;
            box-sizing: border-box;
        }
        .slide h1 { color: ${customizations.primaryColor}; font-size: 36px; margin-bottom: 40px; }
        .slide h2 { color: ${customizations.accentColor}; font-size: 32px; margin-bottom: 30px; }
        .slide ul { font-size: 24px; line-height: 1.6; }
        .slide li { margin-bottom: 15px; }
        .title-slide { text-align: center; }
        .title-slide h1 { font-size: 48px; margin-bottom: 20px; }
        .title-slide .subtitle { font-size: 24px; color: #666; }
        @media print { .slide { page-break-after: always; } }
    </style>
</head>
<body>
    <div class="slide title-slide">
        <h1>${title}</h1>
        <div class="subtitle">Political Campaign Strategic Analysis</div>
        <div class="subtitle">${new Date().toLocaleDateString()}</div>
    </div>
    
    ${slides.map(slide => `
        <div class="slide">
            <h2>${slide.title}</h2>
            <ul>
                ${slide.points.map(point => `<li>${point}</li>`).join('')}
            </ul>
        </div>
    `).join('')}
    
    <script>
        console.log("Presentation slides generated. Copy content to PowerPoint or print as PDF.");
    </script>
</body>
</html>
  `;
}

export type ProfessionalExportFormat = 'pdf' | 'docx' | 'pptx';

export async function exportProfessionalDocument(
  options: ProfessionalExportOptions,
  format: ProfessionalExportFormat,
  fileName: string
): Promise<string> {
  const baseFileName = fileName.replace(/\.(pdf|docx|pptx)$/, '');
  
  switch (format) {
    case 'pdf':
      return generateProfessionalPDF(options, `${baseFileName}.pdf`);
    case 'docx':
      return generateProfessionalDOCX(options, `${baseFileName}.docx`);
    case 'pptx':
      return generateProfessionalPPTX(options, `${baseFileName}.pptx`);
    default:
      throw new Error(`Unsupported format: ${format}`);
  }
}
